import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, input, inject } from '@angular/core';
import { FormControl, FormGroup, NgControl, ValidationErrors } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ContainsKeyPipe } from 'projects/login/src/app/utils/contains-key.pipe';
import { Observable, Subject, takeUntil } from 'rxjs';
import { FormErrorComponent } from '../form-error/form-error.component';
import { HasControlErrorPipe } from './has-control-error.pipe';

@Component({
  selector: 'pay-field-multi-validator',
  imports: [FormErrorComponent, TranslateModule, NgClass, HasControlErrorPipe, ContainsKeyPipe],
  templateUrl: './field-multi-validator.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldMultiValidatorComponent implements OnInit, OnDestroy {
  private readonly _cdRef = inject(ChangeDetectorRef);

  private _ngControlName: string | number | null | undefined;

  set ngControlName(value: string | number | null) {
    this._ngControlName = value;
  }

  get ngControlName(): string | number | null | undefined {
    return this._ngControlName;
  }

  private _touched: boolean = false;

  get touched() {
    return this._touched;
  }

  set touched(value: boolean) {
    if (this._touched === value) {
      return;
    }

    this._touched = value;
  }

  private _invalid: boolean = false;

  get invalid() {
    return this._invalid;
  }

  set invalid(value: boolean) {
    if (this._invalid === value) {
      return;
    }

    this._invalid = value;
  }

  private _ngControl: NgControl[] | FormControl[] | undefined;

  get ngControl(): NgControl[] | FormControl[] | undefined {
    return this._ngControl;
  }

  @Input({ required: true }) set ngControl(value: NgControl[] | FormControl[]) {
    this._ngControl = value;
  }

  private _currentErrors: ValidationErrors | null = [];

  set currentErrors(value: ValidationErrors | null) {
    this._currentErrors = { ...value };
  }

  get currentErrors(): ValidationErrors | null {
    return this._currentErrors;
  }

  readonly pendingObs = input.required<Observable<NgControl | FormControl>>();

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor() {
    let controlName = '';

    this.ngControl?.forEach((control) => {
      if (control instanceof FormControl) {
        let formControlName = null;
        const parentControl = control.parent;

        if (parentControl instanceof FormGroup) {
          Object.keys(parentControl.controls).forEach((controlName) => {
            if (control === parentControl.controls[controlName]) {
              formControlName = controlName;
            }
          });
        }
        controlName += '-' + formControlName;
      } else {
        controlName += '-' + control.name;
      }
    });

    this.ngControlName = controlName;
  }

  ngOnInit() {
    this.ngControl?.forEach((control) => {
      control?.valueChanges?.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.handleChange(control);
      });
    });

    this.pendingObs()
      ?.pipe(takeUntil(this.destroy$))
      .subscribe((control) => {
        this.handleChange(control);
      });
  }

  handleChange(control: NgControl | FormControl) {
    if (!control) {
      this.currentErrors = null;
      return;
    }

    this.touched = !!control.touched;
    this.invalid = !!control.invalid;

    if (this.invalid) {
      this.ngControl?.forEach((control) => {
        this.currentErrors = control.errors;
      });
    } else {
      this.currentErrors = null;
    }

    this._cdRef.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  getError(errorCode: string) {
    return this.ngControl?.find((control) => control.hasError(errorCode))?.getError(errorCode);
  }
}
